import {
  classifyFromRange,
  classifyToRange,
  RELATIVE_CLASSIFIED_ALL_TIME_RANGE
} from "views/components/time-range-picker/RelativeTimeRangeClassifiedHelper";
import {
  normalizeFromPickerForSearchBar,
  normalizeFromSearchBarForBackend
} from "views/logic/queries/NormalizeTimeRange";
import { readableRange } from "views/logic/queries/TimeRangeToString";
import { isTypeRelativeWithStartOnly } from "views/typeGuards/timeRange";
export const DATE_SEPARATOR = "><";
export const TIME_RANGE_TYPE_SEPARATOR = "@";
export const extractRangeFromString = (timeRange) => timeRange.split(DATE_SEPARATOR);
const decodeKeywordValue = (value) => decodeURIComponent(value.replace(/\+/g, "%20"));
const decodeAbsoluteValue = (value) => decodeURIComponent(value);
const isNumericRangeValue = (value) => value !== void 0 && value !== "" && Number.isFinite(Number(value));
const parseRelativeTimeRange = (value) => {
  const [from, to] = value.split(DATE_SEPARATOR);
  const fromRange = Number(from);
  if (!Number.isFinite(fromRange)) {
    throw new Error(`Invalid relative time range value: ${value}`);
  }
  if (to !== void 0 && to !== "") {
    const toRange = Number(to);
    return {
      type: "relative",
      from: fromRange,
      ...Number.isFinite(toRange) && { to: toRange }
    };
  }
  return {
    type: "relative",
    range: fromRange
  };
};
const parseAbsoluteTimeRange = (value) => {
  const [from, to = ""] = value.split(DATE_SEPARATOR);
  return {
    type: "absolute",
    from: decodeAbsoluteValue(from),
    to: decodeAbsoluteValue(to)
  };
};
const parseUntypedTimeRange = (value) => {
  const ranges = value.split(DATE_SEPARATOR);
  const isRelative = ranges.every((range) => range === "" || isNumericRangeValue(range));
  return isRelative ? parseRelativeTimeRange(value) : parseAbsoluteTimeRange(value);
};
export const parseStringTimerangeFilterValue = (filterValue) => {
  const separatorIndex = filterValue.indexOf(TIME_RANGE_TYPE_SEPARATOR);
  if (separatorIndex < 0) {
    return parseUntypedTimeRange(filterValue);
  }
  const type = filterValue.slice(0, separatorIndex);
  const value = filterValue.slice(separatorIndex + TIME_RANGE_TYPE_SEPARATOR.length);
  switch (type) {
    case "relative":
      return parseRelativeTimeRange(value);
    case "keyword":
      return {
        type,
        keyword: decodeKeywordValue(value)
      };
    case "absolute":
      return parseAbsoluteTimeRange(value);
    default:
      return parseUntypedTimeRange(filterValue);
  }
};
const relativeTimeRangeTitle = (timeRange) => {
  if (isTypeRelativeWithStartOnly(timeRange) && timeRange.range === 0) {
    return "All time";
  }
  const from = isTypeRelativeWithStartOnly(timeRange) ? readableRange(timeRange, "range") : readableRange(timeRange, "from");
  const until = "to" in timeRange ? readableRange(timeRange, "to", "now") : "now";
  return `from ${from} until ${until}`;
};
const absoluteTimeRangeTitle = (timeRange, formatTime) => {
  const from = timeRange.from ? formatTime(timeRange.from) : "All time";
  const until = timeRange.to ? formatTime(timeRange.to) : "Now";
  return `${from} - ${until}`;
};
const defaultInitialValues = () => ({
  timeRangeTabs: {
    relative: {
      type: "relative",
      from: {
        value: 5,
        unit: "minutes",
        isAllTime: false
      },
      to: RELATIVE_CLASSIFIED_ALL_TIME_RANGE
    }
  },
  activeTab: "relative"
});
const relativeTimeRangeToFormValue = (timeRange) => {
  if (isTypeRelativeWithStartOnly(timeRange)) {
    return {
      type: "relative",
      from: classifyFromRange(timeRange.range),
      to: RELATIVE_CLASSIFIED_ALL_TIME_RANGE
    };
  }
  return {
    type: "relative",
    from: classifyFromRange(timeRange.from),
    to: typeof timeRange.to === "number" ? classifyToRange(timeRange.to) : RELATIVE_CLASSIFIED_ALL_TIME_RANGE
  };
};
const absoluteTimeRangeToFormValue = (timeRange, formatTime) => ({
  type: "absolute",
  from: timeRange.from ? formatTime(timeRange.from, "complete") : "",
  to: timeRange.to ? formatTime(timeRange.to, "complete") : formatTime(/* @__PURE__ */ new Date(), "complete")
});
const timeRangeToFormValues = (timeRange, formatTime) => {
  switch (timeRange.type) {
    case "relative":
      return {
        timeRangeTabs: {
          relative: relativeTimeRangeToFormValue(timeRange)
        },
        activeTab: "relative"
      };
    case "keyword":
      return {
        timeRangeTabs: {
          keyword: timeRange
        },
        activeTab: "keyword"
      };
    case "absolute":
      return {
        timeRangeTabs: {
          absolute: absoluteTimeRangeToFormValue(timeRange, formatTime)
        },
        activeTab: "absolute"
      };
    default:
      throw new Error(`Invalid time range type: ${timeRange}`);
  }
};
const serializeTimeRange = (timeRange) => {
  if (!timeRange) {
    return "";
  }
  switch (timeRange.type) {
    case "relative":
      if ("range" in timeRange) {
        return `relative${TIME_RANGE_TYPE_SEPARATOR}${timeRange.range}`;
      }
      if (typeof timeRange.to === "number") {
        return `relative${TIME_RANGE_TYPE_SEPARATOR}${timeRange.from}${DATE_SEPARATOR}${timeRange.to}`;
      }
      return `relative${TIME_RANGE_TYPE_SEPARATOR}${timeRange.from}`;
    case "keyword":
      return `keyword${TIME_RANGE_TYPE_SEPARATOR}${timeRange.keyword}`;
    case "absolute":
      return `absolute${TIME_RANGE_TYPE_SEPARATOR}${timeRange.from}${DATE_SEPARATOR}${timeRange.to}`;
    default:
      throw new Error(`Invalid time range type: ${timeRange}`);
  }
};
export const timeRangeTitle = (timeRange, formatTime) => {
  if (!timeRange) {
    return "";
  }
  const parsedTimeRange = typeof timeRange === "string" ? parseStringTimerangeFilterValue(timeRange) : timeRange;
  switch (parsedTimeRange.type) {
    case "relative":
      return relativeTimeRangeTitle(parsedTimeRange);
    case "keyword":
      return parsedTimeRange.keyword;
    case "absolute":
      return absoluteTimeRangeTitle(parsedTimeRange, formatTime);
    default:
      throw new Error(`Invalid time range type: ${parsedTimeRange}`);
  }
};
export const filterValueToTimeRangePickerFormValues = (filterValue, formatTime) => {
  if (!filterValue) {
    return defaultInitialValues();
  }
  return timeRangeToFormValues(parseStringTimerangeFilterValue(filterValue), formatTime);
};
export const timeRangePickerFormValuesToFilterValue = ({ timeRangeTabs, activeTab }, userTimezone, formatTime) => {
  const activeTimeRange = activeTab ? timeRangeTabs[activeTab] : void 0;
  const searchBarTimeRange = normalizeFromPickerForSearchBar(activeTimeRange);
  const backendTimeRange = normalizeFromSearchBarForBackend(searchBarTimeRange, userTimezone);
  return {
    title: timeRangeTitle(backendTimeRange, formatTime),
    value: serializeTimeRange(backendTimeRange)
  };
};
